// vipAdvisor bound to the bundled game data: each class's kit (a human's skillset with its
// roll chances, a monster's intrinsic skills), each forceable skillset's kit as a secondary,
// and the champion units' defenses from their gear and class tips. The pure logic and its
// tests are in vipAdvisor.js.
import genPools from '../data/genPools.json';
import abilityRates from '../data/abilityRates.json';
import monsterSkills from '../data/monsterSkills.json';
import abilitiesBackup from '../data/abilities.backup.json';
import itemsBackup from '../data/items.backup.json';
import tipsBackup from '../data/tips.backup.json';
import genTokens from '../data/genTokens.json';
import { kitProfile, unitDefenses } from './vipAdvisor.js';

let ABILITY = null, ITEM = null;
const abilityInfo = () => ABILITY || (ABILITY = Object.fromEntries(abilitiesBackup.abilities.map((a) => [a.name, a.info])));
const itemInfo = () => ITEM || (ITEM = Object.fromEntries(itemsBackup.items.map((i) => [i.name, i.info])));
// An ability's roll chance (the mined mode), or an even chance when the logs never saw it.
const chanceOf = (rates, name) => { const r = rates && rates[name]; return r ? r.mode / 100 : 0.5; };
const skillsetKitOf = (cls, which) => kitProfile(
  genPools.skillsets[cls].abilities.map((name) => ({ name, chance: chanceOf((abilityRates[cls] || {})[which], name) })),
  abilityInfo(),
);

// A class's own kit: a human's skillset as its primary, or a monster's intrinsic skills (a
// starred one is locked behind Beastmaster, so it's left out, as the battle sim does).
const KITS = new Map();
export function classKit(cls) {
  if (!KITS.has(cls)) {
    const set = genPools.skillsets[cls];
    KITS.set(cls, set && set.skillset
      ? skillsetKitOf(cls, 'primary')
      : kitProfile((monsterSkills[cls] || []).filter((s) => !s.endsWith('*')).map((name) => ({ name, chance: 1 })), abilityInfo()));
  }
  return KITS.get(cls);
}

// The skillset a class has as its primary (Wizard → "Black Magic"); '' for monsters and Mime.
export const ownSkillset = (cls) => (genPools.skillsets[cls] && genPools.skillsets[cls].skillset) || '';

// A forceable skillset token's kit when forced as a secondary, or null if it isn't one
// (Blue Magic has no class of its own to read it from).
const SKILLSET_CLASS = {};
for (const [cls, s] of Object.entries(genPools.skillsets)) if (s && s.skillset) SKILLSET_CLASS[s.skillset] = cls;
const SKILLSET_KITS = new Map();
export function skillsetKit(token) {
  const meta = genTokens.byToken[token];
  const cls = meta && meta.category === 'skillset' ? SKILLSET_CLASS[meta.name] : '';
  if (!cls) return null;
  if (!SKILLSET_KITS.has(token)) SKILLSET_KITS.set(token, { name: meta.name, kit: skillsetKitOf(cls, 'secondary') });
  return SKILLSET_KITS.get(token);
}

// A unit's class tip: humans are keyed by class and gender ("TimeMage Female"), monsters by
// their name without spaces ("SteelGiant").
export function classTip(unit) {
  const key = String(unit.Class || '').replace(/ /g, '');
  const tips = tipsBackup.Class || {};
  return tips[key + ' ' + (unit.Gender === 'Female' ? 'Female' : 'Male')] || tips[key] || '';
}

export const defensesOf = (unit) => unitDefenses(unit, itemInfo(), classTip(unit));
